import { AddNewNodeParam, CreateNewNodeParam, HomeUsePluginParam } from "@uikit/project/HomeUsePluginData";
import {
  StudioProjectAttributesV2EdgeData,
  StudioProjectAttributesV2EdgeDataLineParam,
  StudioProjectV3EdgeFlow,
} from "imagica-corekit/dist/base/project/domain/StudioProjectAttributesV2Edge";
import { StudioProjectAttributesV3Line } from "imagica-corekit/dist/base/project/domain/StudioProjectAttributesV3Line";
import { StudioProjectAttributesV3Node } from "imagica-corekit/dist/base/project/domain/StudioProjectAttributesV3Node";
import { StoryNodeValidator } from "imagica-corekit/dist/base/util/StoryNodeValidator";
import { useStore } from "react-redux";
import { Node } from "reactflow";

import func from "@uikit/func";
import { getIt } from "@uikit/getIt";
import { CanvasDataRef } from "@uikit/model/CanvasDataRef";
import { HomeUsePluginUtil } from "@uikit/util/HomeUsePluginUtil";
import { ReactFlowNodeUtil } from "@uikit/util/ReactFlowNodeUtil";
import { previewStore } from "@uiview/store/PreviewStore";
import { initialReportSizeMapping } from "@uiview/views/Nodes/nodeConfig";
import { NodeConfigUtil } from "@uiview/views/Nodes/NodeConfigUtil";
import { DisplayType } from "@uiview/views/Nodes/typing";
import { CreatorAISaasStore } from "@uiview/views/ProjectCanvas/store/CreatorAISassStore";
import { CreatorNodesStore } from "@uiview/views/ProjectCanvas/store/CreatorNodesStore";
import { creatorRefStore } from "@uiview/views/ProjectCanvas/store/CreatorRefStore";
import { projectPageStore } from "@uiview/views/ProjectCanvas/store/ProjectPageStore";
import { CustomNodeUtil } from "@views/thinking-layout-editor/CustomNodeUtil";
import { sleep } from "@views/thinking-layout-editor/util";
import { ProjectUtil } from "imagica-corekit/dist/cases/util/ProjectUtil";
import { find, isEmpty, uniqBy } from "lodash";
import { CreatorEdgesStore } from "@uiview/views/ProjectCanvas/store/CreatorEdgesStore";

import { GROUPID_REG, NEW_EDGE_REG, OFFSET_DISTANCE } from "../../views/thinking-layout-editor/constants";
import { CanvasEdgeUtil } from "@uikit/util/CanvasEdgeUtil";
import { CanvasLineUtil } from "@uikit/util/CanvasLineUtil";
import { ProjectNodeStore } from "@uikit/projectNode/ProjectNodeStore";
import { CreatorSaasAppStore } from "@uiview/views/ProjectCanvas/store/CreatorSaasAppStore";

// eslint-disable-next-line
export default function useHomeUsePluginUtil({ distance, getEdgeGeneration, getSaasAction }: HomeUsePluginParam) {
  const creatorAISassStore = getIt(CreatorAISaasStore);
  const creatorNodesStore = getIt(CreatorNodesStore);
  const creatorEdgesStore = getIt(CreatorEdgesStore);
  const projectNodeStore = getIt(ProjectNodeStore);
  const creatorSaasAppStore = getIt(CreatorSaasAppStore);

  const store = useStore();

  const canvasDataRef = getIt(CanvasDataRef);

  // 创建新的target node
  const createNewNode = ({ edgeNode, isStickie }: CreateNewNodeParam): Node => {
    const newNodeId = `editor-${creatorRefStore.nodeIndexRef.current++}`;
    const newNode = {
      id: newNodeId,
      type: "customNode",
      data: Object.assign(
        {},
        { ...canvasDataRef.nodeDataRef.current },
        {
          displayType: isStickie ? "inputTextarea" : "text",
          showFeedback: false,
        }
      ),
      position: {
        x: edgeNode.position.x + OFFSET_DISTANCE,
        y: edgeNode.position.y,
      },
    };
    creatorNodesStore.handleUpdateTarget({
      newNode,
      edgeNode,
    });
    return newNode;
  };

  const clickAddNewNode = ({
    direction,
    prevNode,
    isStickie,
  }: AddNewNodeParam): {
    newEdge: Node;
    sourceLine: any;
    targetLine: any;
  } => {
    const newEdge = CanvasEdgeUtil.createNewEdge(prevNode);
    const newNode = createNewNode({
      direction,
      edgeNode: newEdge,
      isStickie,
    });
    const sourceLine = CanvasLineUtil.createNewLine({
      source: prevNode.id,
      target: newEdge.id,
      type: "source",
    });
    const targetLine = CanvasLineUtil.createNewLine({
      sourceNode: prevNode,
      source: newEdge.id,
      target: newNode.id,
      type: "target",
    });
    return {
      newEdge,
      sourceLine,
      targetLine,
    };
  };

  const handleProjectDataV3 = (
    selectedExistingProject: any
  ): {
    nodeIndex: any;
    edgeIndex: any;
    nodesFromProject: any;
    edgesFromProject: any;
    UIData: any;
    variablesList: any;
    customFunctions: any;
  } => {
    let nodesFromProject = [];
    if (typeof selectedExistingProject?.v3?.nodes === "string") {
      nodesFromProject = JSON.parse(selectedExistingProject?.v3?.nodes);
    }
    if (Array.isArray(selectedExistingProject?.v3?.nodes)) {
      nodesFromProject = selectedExistingProject?.v3?.nodes;
    }

    let edgesFromProject = [];
    if (typeof selectedExistingProject?.v3?.edges === "string") {
      edgesFromProject = JSON.parse(selectedExistingProject?.v3?.edges);
    }
    if (Array.isArray(selectedExistingProject?.v3?.edges)) {
      edgesFromProject = selectedExistingProject?.v3?.edges;
    }

    let variablesList = [];
    if (typeof selectedExistingProject?.v3?.variablesList === "string") {
      variablesList = JSON.parse(selectedExistingProject?.v3?.variablesList);
    }

    let UIData = [];
    if (!func.isEmpty(selectedExistingProject?.v3?.UI)) {
      UIData = selectedExistingProject.v3.UI.map((x: any) => {
        let inputObj = x.input;
        let outputArr = x.output;
        if (!func.isEmpty(x.input)) {
          if (typeof x.input === "string") {
            inputObj = JSON.parse(x.input);
          } else {
            inputObj = inputObj.map((x: any) => {
              const nodeData = HomeUsePluginUtil.getFunctionData(canvasDataRef.nodeDataRef.current);
              const data = Object.assign({}, x.data, { ...nodeData });
              return Object.assign({}, x, { data });
            });
          }
        }
        if (!func.isEmpty(x.output) && typeof x.output === "string") {
          outputArr = JSON.parse(x.output);
        }

        let titleStyle = {};
        if (!func.isEmpty(x.titleStyle) && typeof x.titleStyle === "string") {
          titleStyle = JSON.parse(x.titleStyle);
        }

        let subTitleStyle = {};
        if (!func.isEmpty(x.subTitleStyle) && typeof x.subTitleStyle === "string") {
          subTitleStyle = JSON.parse(x.subTitleStyle);
        }

        let showTitle = x?.showTitle;
        if (typeof x?.showTitle != "boolean") {
          showTitle = true;
        }
        let showSubTitle = x?.showSubTitle;
        if (typeof x?.showSubTitle != "boolean") {
          showSubTitle = true;
        }

        return {
          ...x,
          input: inputObj,
          output: outputArr,
          titleStyle,
          subTitleStyle,
          showTitle,
          showSubTitle,
        };
      });
    }

    const nodeIndex = selectedExistingProject.v3?.nodeIndex;
    const edgeIndex = selectedExistingProject.v3?.edgeIndex;
    if (!nodesFromProject || !edgesFromProject || isNaN(nodeIndex) || isNaN(edgeIndex)) {
      throw Error("Failed to load from project, incorrect data format");
    }
    nodesFromProject = nodesFromProject.map((x: any) => {
      let text = x.data?.textAreaValue;
      if (typeof text === "string") {
        text = x.data?.textAreaValue.trim();
      }
      let nodeData = {};
      if (GROUPID_REG.test(x.id)) {
        nodeData = canvasDataRef.groupDataRef.current;
      } else {
        nodeData = NEW_EDGE_REG.test(x.id) ? canvasDataRef.newEdgeDataRef.current : canvasDataRef.nodeDataRef.current;
      }
      const data = Object.assign({}, { ...nodeData }, x.data, { textAreaValue: text });
      return Object.assign({}, x, { data });
    });

    edgesFromProject = edgesFromProject.map((x: any) => {
      const data = Object.assign({}, { ...getIt(CanvasDataRef).newLineDataRef.current }, x.data);
      return Object.assign({}, x, {
        // 由于edge连接形式更改，兼容旧数据指定默认edge起始/终点为左右连接
        sourceHandle: func.isEmpty(x.sourceHandle) ? "b" : x.sourceHandle,
        targetHandle: func.isEmpty(x.targetHandle) ? "a" : x.targetHandle,
        data,
        type: "customNewLine",
      });
    });
    const hasOldSliceData = edgesFromProject.some((x: any) => x.data.isCreatedBySlice && !GROUPID_REG.test(x.target));
    if (hasOldSliceData) {
      func.customMsg({
        content:
          "Your project is using a older version of sharding that is no longer supported, please remove your splitted nodes and redo sharding for your nodes.",
        type: "warning",
      });
    }

    let customFunctions = [];
    if (!func.isEmpty(selectedExistingProject?.v3?.functions)) {
      customFunctions = selectedExistingProject.v3.functions.map((x: any) => {
        if (!func.isEmpty(x.edgeArr)) {
          const newEdgeArr = x.edgeArr.map((y: any) => {
            return {
              ...y,
              data: {
                ...getIt(CanvasDataRef).newLineDataRef.current,
                ...y.data,
              },
            };
          });
          return {
            ...x,
            edgeArr: newEdgeArr,
          };
        }
        return x;
      });
    }

    return {
      nodeIndex,
      edgeIndex,
      nodesFromProject,
      edgesFromProject,
      UIData,
      variablesList,
      customFunctions,
    };
  };

  async function setEdgeValueByWriterV3(id: string, enterText: string, isWriter: boolean = false): Promise<void> {
    const write = async (i: number): Promise<void> => {
      await sleep(20);
      const txt = isWriter && i < 60 ? enterText.slice(0, i) : enterText;
      creatorNodesStore.setNodes((prevList: any) => {
        return prevList.map((l: any) => {
          if (l.id === id) {
            return {
              ...l,
              data: {
                ...l.data,
                lineParam: {
                  ...l.data.lineParam,
                  enterText: txt,
                },
              },
            };
          }
          return l;
        });
      });
      const needNext = i < enterText.length && txt.length < enterText.length && isWriter;
      if (needNext) {
        await write(i + 2);
      }
    };
    await write(1);
  }

  async function getAndSetQueryV3(
    id: string,
    text: string,
    isWriter: boolean = false
  ): Promise<{ lineType: string; identifier: any }> {
    try {
      // todo: 这里需要更新node边数据
      creatorNodesStore.setQueryLoadingV3({
        id,
        data: {
          getQueryLoading: true,
        },
      });
      const identifier = (await getEdgeGeneration(text, id)) as unknown as string;
      creatorNodesStore.setQueryLoadingV3({
        id,
        data: {
          getQueryLoading: false,
        },
      });
      if (identifier) {
        creatorNodesStore.setNodes((prevList: any) => {
          return prevList.map((l: any) => {
            if (l.id === id) {
              return {
                ...l,
                data: {
                  ...l.data,
                  lineParam: {
                    ...l.data.lineParam,
                    lineType: "identifier",
                    identifier,
                  },
                },
              };
            }
            return l;
          });
        });
        creatorNodesStore.setQueryLoadingV3({
          id,
          data: {
            isGetQuery: true,
          },
        });
        await setEdgeValueByWriterV3(id, text, isWriter);
      }
      return {
        lineType: "identifier",
        identifier,
      };
    } catch (error) {
      func.messageError(error);
      console.error("get And Set Query", error);
      creatorNodesStore.setQueryLoadingV3({
        id,
        data: {
          isGetQuery: false,
          getQueryLoading: false,
        },
      });
      return Promise.reject(error);
    }
  }

  const handleCustomToGroup = (preNodeId: string, newNodeArr: Array<any>): void => {
    const nodes = creatorNodesStore.getNodes();
    const currentEdge: any = nodes.find(x => x.data.targetNodeId === preNodeId);
    const currentLine: any = creatorEdgesStore.getEdges().find(line => line.target === preNodeId);
    const preTargetNode = nodes.find(x => x.id === preNodeId);
    const childNodes = newNodeArr.filter(x => x.type !== "customGroup");
    const groupNode = newNodeArr.find(x => x.type === "customGroup");
    if (
      func.isEmpty(currentEdge) ||
      func.isEmpty(currentLine) ||
      func.isEmpty(preTargetNode) ||
      func.isEmpty(childNodes) ||
      func.isEmpty(groupNode)
    )
      return;

    let newArr: Array<any> = [];
    //子node计算position
    let bottom = 0;
    childNodes.forEach(node => {
      const oldChildNode = find(nodes, item => item.id === node.id);
      const tempNode = ReactFlowNodeUtil.getNodeById(node.id, nodes);
      const childNode = {
        ...node,
        data: {
          ...canvasDataRef.nodeDataRef.current,
          // stud-2493 set generated label(nodes)
          ...(oldChildNode ? { generatedLabel: oldChildNode.data?.generatedLabel } : {}),
          ...node.data,
        },
        width: tempNode?.width,
        height: tempNode?.height,
        position: {
          x: distance,
          y: distance + bottom + CustomNodeUtil.titleDistance,
        },
      };
      let tempHeight = tempNode?.height;
      if (tempHeight === undefined || tempHeight === null) {
        const nodeLayout = NodeConfigUtil.getNodeLayoutByType(node.data.displayType as DisplayType);
        tempHeight = nodeLayout.height;
        if (node.data.displayType === "text") {
          tempHeight = nodeLayout.minHeight ?? nodeLayout.height;
        }
      }
      bottom += tempHeight + distance / 2;
      newArr.push(childNode);
    });
    //group计算高宽
    const groupHeight =
      newArr
        .map(x => {
          let tempHeight = x?.height;
          if (tempHeight === undefined) {
            const nodeLayout = NodeConfigUtil.getNodeLayoutByType(x.data.displayType as DisplayType);
            tempHeight = nodeLayout.height;
            if (x.data.displayType === "text") {
              return nodeLayout.minHeight ?? tempHeight;
            }
          }
          return tempHeight;
        })
        .reduce((total, x) => total + x + distance / 2, distance) + projectPageStore.state.previewHeight;
    const maxX = Math.max(
      ...childNodes.map(x => {
        const displayType = x.data?.displayType;
        if (func.isEmpty(displayType)) {
          console.error("DisplayType is empty:", displayType);
          return 0;
        }
        return initialReportSizeMapping[displayType as DisplayType]?.width || 0;
      })
    );
    const groupWidth = maxX + 2 * distance;
    newArr.unshift({
      ...groupNode,
      data: {
        ...canvasDataRef.groupDataRef.current,
        ...groupNode.data,
        // stud-2493 set generated label(nodes)
        ...(preTargetNode?.id === groupNode.id ? { generatedLabel: preTargetNode?.data?.generatedLabel } : {}),
      },
      style: {
        width: groupWidth,
        height: groupHeight + CustomNodeUtil.collapseDistance,
      },
      position: {
        x: preTargetNode?.position?.x,
        y: preTargetNode?.position?.y,
      },
    });
    //替换node、边上的targetNodeId
    creatorNodesStore.setNodes((prevList: any) => {
      const filterList = prevList.filter((x: any) => x.id !== preNodeId && x.parentNode !== preNodeId);
      const updateList = filterList.map((x: any) => {
        if (x.id === currentEdge.id) {
          return {
            ...x,
            data: {
              ...x.data,
              targetNodeId: groupNode.id,
            },
          };
        }
        // 判断new array 在updateList已经存在就不重复添加
        newArr = newArr.filter((y: any) => y.id !== x.id);
        return x;
      });
      return [...updateList, ...newArr];
    });
    //替换线的target
    creatorEdgesStore.setEdges((prevList: any) => {
      return prevList.map((line: any) => {
        if (line.id === currentLine.id) {
          return {
            ...line,
            target: groupNode.id,
          };
        }
        return line;
      });
    });
  };

  const handleGroupToCustom = (preNodeId: string, newNode: any): void => {
    const currentEdge: any = creatorNodesStore.getNodes().find(x => x.data.targetNodeId === preNodeId);
    const preTargetNode = creatorNodesStore.getNodes().find(x => x.id === preNodeId);
    if (func.isEmpty(currentEdge) || func.isEmpty(preTargetNode) || func.isEmpty(newNode)) return;

    const node = {
      id: newNode.id,
      type: "customNode",
      data: Object.assign({}, { ...canvasDataRef.nodeDataRef.current }, newNode.data),
      position: {
        x: preTargetNode?.position?.x,
        y: preTargetNode?.position?.y,
      },
    };

    /// 替换node、边上的targetNodeId
    creatorNodesStore.setNodes((prevList: any) => {
      const filterList = prevList.filter((x: any) => x.id !== preNodeId && x.parentNode !== preNodeId);
      const updateList = filterList.map((x: any) => {
        if (x.id === currentEdge.id) {
          return {
            ...x,
            data: {
              ...x.data,
              targetNodeId: newNode.id,
            },
          };
        }
        return x;
      });
      return [...updateList, node];
    });

    /// 替换线的target
    creatorEdgesStore.setEdges((prevList: any) => {
      return prevList.map((line: any) => {
        if (line.target === preNodeId) {
          return {
            ...line,
            target: node.id,
          };
        }
        return line;
      });
    });

    /// 删除node后面的线和边
    const allNodes = creatorNodesStore.getNodes() as StudioProjectAttributesV3Node[];
    const allEdges = creatorEdgesStore.getEdges() as any as any[];
    const deleteElements = ProjectUtil.nodeRightRelations(preNodeId, allNodes, allEdges);
    if (deleteElements.length !== 0) {
      creatorEdgesStore.setEdges((prevList: any) => {
        const newList = prevList.filter((line: any) => {
          return !deleteElements.includes(line.id);
        });
        return newList;
      });
      creatorNodesStore.setNodes((prevList: any) => {
        const newList = prevList.filter((edge: any) => {
          return !deleteElements.includes(edge.id);
        });
        return newList;
      });
    }
  };

  function getNewEdgeAllLineIdsByNode(node: any): any[] {
    const edges = creatorEdgesStore.getEdges() as unknown as any[];
    const lines = edges.filter(x => x.source === node.id || x.target === node.id);
    return lines.map(x => x.id);
  }
  function getNewEdgeDataByLine(line: any): {
    edge: any;
    lineIds: any[];
  } {
    const source = line.source;
    const nodes = creatorNodesStore.getNodes() as unknown as any[];

    if (NEW_EDGE_REG.test(source)) {
      /// 选中为targetLine, 返回edge和[sourceLine,targetLine]
      const edge = nodes.find(node => node.id === source);
      return {
        edge: edge,
        lineIds: [line.id, ...edge.data.flows.map((x: any) => x.sourceLineId)],
      };
    } else {
      // 边
      const target = line.target;
      const edge = nodes.find(node => node.id === target);
      return {
        edge: edge,
        lineIds: [line.id, edge.data.targetLineId],
      };
    }
  }

  function getSourceLinesByEdge(
    currEdge: StudioProjectAttributesV3Node,
    oldEdges: StudioProjectAttributesV3Line[]
  ): StudioProjectAttributesV3Line[] {
    return oldEdges.filter(
      (edge: StudioProjectAttributesV3Line) =>
        currEdge.data &&
        (currEdge.data as StudioProjectAttributesV2EdgeData).flows?.some(
          (f: StudioProjectV3EdgeFlow) => f.sourceLineId === edge.id
        )
    );
  }
  // function getEdgeRelevant({
  //   nodes,
  //   queryNodeId,
  //   oldEdges
  // }: {
  //   nodes: StudioProjectAttributesV3Node[]
  //   queryNodeId: string
  //   oldEdges: StudioProjectAttributesV3Line[]
  // }): any {
  //   const currEdge = nodes.find((node: StudioProjectAttributesV3Node) => node.id === queryNodeId)
  //   const currEdgeData = currEdge?.data as StudioProjectAttributesV2EdgeData
  //   if(!currEdge || !currEdgeData || !currEdgeData?.flows) return;
  //   const sourceLines = getSourceLinesByEdge(currEdge, oldEdges)

  //   const targetLine = oldEdges.find((edge: StudioProjectAttributesV3Line) =>
  //     edge.source === currEdgeData.targetLineId
  //   )
  //   const newDelEdges = [...sourceLines, targetLine]

  //   return {
  //     currEdge,
  //     currEdgeData,
  //     newDelEdges
  //   }
  // }
  // 修改边flows对应数据
  function getFlows(flows: StudioProjectV3EdgeFlow[], id: string): StudioProjectV3EdgeFlow[] {
    let newFlows: StudioProjectV3EdgeFlow[] = flows.filter((f: StudioProjectV3EdgeFlow) => f.sourceNodeId !== id);
    // 删除了主node 设置下一个为主node
    if (newFlows.every(x => !x.creationMethod)) {
      newFlows = newFlows.map((y: any, index: number) => {
        if (index === 0) {
          return {
            ...y,
            creationMethod: true,
          };
        }
        return y;
      });
    }
    return newFlows;
  }
  function getVarilableNames(lineParam: StudioProjectAttributesV2EdgeDataLineParam, id: string): string[] {
    const varilableNames: string[] = lineParam?.varilableNames || [];
    const variableList = (store.getState() as any).fot.variableList || [];

    const variableNodeData = variableList.find((x: any) => x?.node?.id === id);
    if (func.isEmpty(variableNodeData)) return varilableNames;

    // 找到需要删除的 node 的变量
    return varilableNames.filter(x => x !== variableNodeData.name);
  }
  function delNodesNoTriggerListener(delNodeIds: string[]): void {
    creatorNodesStore.setNodes(prevList => {
      return prevList.filter(n => !delNodeIds.includes(n.id));
    });
  }
  function delEdgesNoTriggerListener(delEdgeIds: string[]): void {
    creatorEdgesStore.setEdges((prevList: StudioProjectAttributesV3Line[]) => {
      return prevList.filter((n: StudioProjectAttributesV3Line) => !delEdgeIds.includes(n.id));
    });
  }
  function handelDelSourceNode(
    currEdge: StudioProjectAttributesV3Node | undefined,
    oldEdges: StudioProjectAttributesV3Line[],
    node: StudioProjectAttributesV3Node,
    delEdgeNodes: (StudioProjectAttributesV3Node | undefined)[],
    delLines: any[]
  ): void {
    const currEdgeData = currEdge?.data as StudioProjectAttributesV2EdgeData;
    if (!currEdge || !currEdgeData || !currEdgeData?.flows) return;
    const sourceLines = getSourceLinesByEdge(currEdge, oldEdges);
    const targetLine = oldEdges.find((edge: StudioProjectAttributesV3Line) => edge.id === currEdgeData.targetLineId);

    // 只有一个source node则需要同时删除边
    if (currEdgeData.flows.length === 1) {
      delNodesNoTriggerListener([currEdge.id]);
      delEdgeNodes.push(currEdge);
      delLines.push(...[...sourceLines, targetLine]);
    } else {
      const delLineId = currEdgeData.flows.find(x => x.sourceNodeId === node.id)?.sourceLineId;
      if (delLineId) {
        const delLine = oldEdges.find((edge: StudioProjectAttributesV3Line) => edge.id === delLineId);
        if (delLine) {
          delLines.push(delLine);
        }
      }
      // 有多个source node，删除某一个source node后则只需要更新对应边保存的sourceId数据
      creatorNodesStore.setNodes(prevList => {
        return prevList.map(n => {
          if (n.id === currEdge.id) {
            const data = n?.data as StudioProjectAttributesV2EdgeData;
            const flows: StudioProjectV3EdgeFlow[] = getFlows(data?.flows as StudioProjectV3EdgeFlow[], node.id);

            const newVarilableNames: string[] = getVarilableNames(
              data?.lineParam as StudioProjectAttributesV2EdgeDataLineParam,
              node.id
            );
            return {
              ...n,
              data: {
                ...n.data,
                flows: flows,
                lineParam: {
                  ...data.lineParam,
                  varilableNames: newVarilableNames,
                },
              },
            };
          }
          return n;
        });
      });
    }
  }

  // todo: 删除group子元素还需兼容
  function onNodesDeleteV3(
    delNodes: StudioProjectAttributesV3Node[],
    oldEdges: StudioProjectAttributesV3Line[],
    oldNodes: any[]
  ): {
    delLines: (StudioProjectAttributesV3Line | undefined)[];
    delEdgeNodes: (StudioProjectAttributesV3Node | undefined)[];
  } {
    const delEdgeNodes: Array<StudioProjectAttributesV3Node | undefined> = [];
    let delLines: Array<StudioProjectAttributesV3Line | undefined> = [];

    delNodes.forEach((node: StudioProjectAttributesV3Node) => {
      // 删除的是source node
      const sourceNodeLines = oldEdges.filter((edge: StudioProjectAttributesV3Line) => edge.source === node.id);
      if (!isEmpty(sourceNodeLines)) {
        const currEdges = oldNodes.filter((x: StudioProjectAttributesV3Node) =>
          sourceNodeLines.some(y => y.target === x.id)
        );
        currEdges.forEach((currEdge: StudioProjectAttributesV3Node) => {
          handelDelSourceNode(currEdge, oldEdges, node, delEdgeNodes, delLines);
        });
      }

      // 删除的是target node，则需要同时删除边
      const targetNodeLine = oldEdges.find((edge: StudioProjectAttributesV3Line) => edge.target === node.id);
      if (!isEmpty(targetNodeLine)) {
        const currEdge = oldNodes.find((node: StudioProjectAttributesV3Node) => node.id === targetNodeLine.source);
        const currEdgeData = currEdge?.data as StudioProjectAttributesV2EdgeData;
        if (!currEdge || !currEdgeData || !currEdgeData?.flows) return;
        const sourceLines = getSourceLinesByEdge(currEdge, oldEdges);
        // 这里可能存在即是 source 又是 target 的节点的line，所以需要结合一下
        delLines = [...delLines, ...sourceLines, targetNodeLine];

        if (!isEmpty(currEdge)) {
          delNodesNoTriggerListener([currEdge.id]);
          delEdgeNodes.push(currEdge);
        }
      }
    });

    delEdgesNoTriggerListener(delLines.map((line: any) => line.id));
    return {
      delLines: uniqBy(delLines, "id"),
      delEdgeNodes: uniqBy(delEdgeNodes, "id"),
    };
  }

  //获取删除后的inputs
  function deleteInputNode(saasDataObj: any, nodeIds: Array<string>): any {
    if (func.isEmpty(saasDataObj)) return [];
    const { oldData, type } = saasDataObj;
    if (type === "thinkingApp") return [];
    const newInput = (oldData.input || []).filter((x: any) => !nodeIds.includes(x.id));
    return newInput;
  }

  function removeInputFeild(nodeIds: Array<string>): void {
    const uiType = previewStore.state.selectedUI;
    const saasDataObj = getSaasAction(uiType);
    const newInputNode = deleteInputNode(saasDataObj, nodeIds);
    const { resetData } = saasDataObj;
    const newInputNodeId = newInputNode.map((x: any) => x.id);
    creatorAISassStore.setInputId(newInputNodeId);
    // stud-2493 set input(preview)(delete)
    resetData((prevData: any) => {
      return {
        ...prevData,
        input: newInputNode,
      };
    });
    projectNodeStore.composeNodes(creatorNodesStore.getNodes(), creatorSaasAppStore.state.saasUIData);
  }

  function autoAddInputNodeByBluePrint({
    edgeNode,
    nodes,
    inputs,
  }: {
    edgeNode: Node;
    nodes: Array<Node<any>>;
    inputs: Array<{ description: string; name: string; type: string }>;
  }): void {
    const flows = edgeNode?.data.flows.map((x: any) => x.sourceNodeId);
    // 先更新现有node的数据
    let maxX = 0;
    let maxY = 0;
    let nodeIndex = 0;
    let newNodes = nodes.map(x => {
      if (flows.includes(x.id)) {
        const currentInput = inputs[nodeIndex];
        let displayType = currentInput.type;
        if (StoryNodeValidator.isValidStoryNodeDisplayType(currentInput.type) === false) {
          displayType = x.data.displayType;
          console.error("The displayType is illegal, please check");
        }

        nodeIndex++;
        if (currentInput) {
          maxX = Math.max(x.position.x, maxX);
          maxY = Math.max(x.position.y, maxY);
          return {
            ...x,
            data: {
              ...x.data,
              // stud-2493 set input title(nodes)
              inputTitle: currentInput.name,
              displayType,
              description: currentInput.description,
            },
          };
        }
      }
      return x;
    });

    const addedFlows: Array<{ sourceNodeId: string; sourceLineId: string; creationMethod: boolean }> = [];
    const addedLines: any[] = [];

    if (nodeIndex < inputs.length) {
      inputs.slice(nodeIndex).forEach(input => {
        const newNodeId = `editor-${creatorRefStore.nodeIndexRef.current++}`;
        let displayType = input.type;
        if (StoryNodeValidator.isValidStoryNodeDisplayType(input.type) === false) {
          displayType = "text";
          console.error("The displayType is illegal, please check");
        }
        const newNode = {
          id: newNodeId,
          type: "customNode",
          data: Object.assign(
            {},
            { ...canvasDataRef.nodeDataRef.current },
            {
              // stud-2493 set input title(nodes)
              inputTitle: input.name,
              displayType,
              description: input.description,
              showFeedback: false,
            }
          ),
          position: {
            x: maxX,
            y: maxY + 400,
          },
        };
        const newLine = CanvasLineUtil.generateNewLine({ source: newNode.id, target: edgeNode.id, type: "source" });
        const newLineParam = { sourceNodeId: newNodeId, sourceLineId: newLine.id, creationMethod: false };
        addedFlows.push(newLineParam);
        newNodes.push(newNode);
        addedLines.push(newLine);
      });
    }

    newNodes = newNodes.map(node => {
      if (node.id === edgeNode.id) {
        return {
          ...node,
          data: {
            ...node.data,
            flows: node.data.flows.concat(addedFlows),
          },
        };
      }
      return node;
    });

    creatorNodesStore.setNodes(newNodes);
    creatorEdgesStore.setEdges((prevList: any) => [...prevList, ...addedLines]);
    projectNodeStore.composeNodes(newNodes, creatorSaasAppStore.state.saasUIData);
  }

  return {
    onNodesDeleteV3,
    clickAddNewNode,
    getAndSetQueryV3,
    setEdgeValueByWriterV3,
    getNewEdgeAllLineIdsByNode,
    getNewEdgeDataByLine,
    handleProjectDataV3,
    handleCustomToGroup,
    // handleV2TransformV3Data
    deleteInputNode,
    removeInputFeild,
    handleGroupToCustom,
    autoAddInputNodeByBluePrint,
  };
}
